import React, { memo, useCallback } from 'react';
import { Button } from 'antd';
import { useDispatch } from 'react-redux';
import Cookies from 'js-cookie';
import { User } from '@context/AuthContext/types';
import type {
  StepCopyItem,
  TourValue
} from '@context/OnboardingProcessProvider';
import { useIsDemoOrganization } from '@hooks/useIsDemoOrganization';
import {
  toggleApprovalAssetModal,
  toggleBootcampModal,
  toggleCampaignFolderCreationModal
} from '@redux/actions/modalAction';
import LottieComponent from '@components/Lottie';
import {
  preferredSubdomainCookieName,
  redirectToSubdomain
} from '@components/OrganizationBoundary';
import { ApprovalsEmptyIcon, CheckIcon, CrossIcon } from '@icons';
import './OnboardingProcess.less';

type OnboardingProcessProps = {
  copies?: StepCopyItem;
  onCloseProcess: () => void;
  onStartTour: (data: TourValue) => void;
  user: User<'internal' | 'external-reviewer'> | null;
};

export default memo(function OnboardingProcess({
  copies,
  onCloseProcess,
  onStartTour,
  user
}: OnboardingProcessProps) {
  const dispatch = useDispatch();
  const isDemoOrganization = useIsDemoOrganization();

  const onContinueOnboarding = useCallback(() => {
    onCloseProcess();
    dispatch(toggleCampaignFolderCreationModal(null));
    dispatch(toggleApprovalAssetModal(null));
    dispatch(
      toggleBootcampModal({ visible: true, defaultTab: 'interactiveTour' })
    );
  }, [dispatch, onCloseProcess]);

  const onCloseOnboardingProcess = useCallback(() => {
    onCloseProcess();
    if (isDemoOrganization)
      redirectToSubdomain(
        Cookies.get(preferredSubdomainCookieName) || 'demo-over',
        'campaigns/all'
      );
  }, [isDemoOrganization, onCloseProcess]);

  if (copies) {
    return (
      <div className="onboarding-process">
        <div className="onboarding-process-blocked" />
        {copies?.isShowConfetti && (
          <LottieComponent view="confetti" className="icon" isLooped={false} />
        )}
        <div className="onboarding-process-wrapper">
          <div className="onboarding-process-wrapper-top">
            <div className="onboarding-process-wrapper-top__icon">
              <ApprovalsEmptyIcon />
            </div>
            <div className="onboarding-process-wrapper-top__copies">
              <h3>{copies?.title}</h3>
              <p>{copies?.description}</p>
            </div>
            <div className="onboarding-process-wrapper-top__close">
              <CrossIcon onClick={onCloseOnboardingProcess} />
            </div>
          </div>
          {copies.image && (
            <div className="onboarding-process-wrapper-image">
              <img src={copies.image} alt={copies?.title} />
            </div>
          )}
          <div className="onboarding-process-wrapper-bottom">
            {copies?.steps && (
              <div className="onboarding-process-wrapper-steps">
                <CheckIcon />
                {copies?.steps}
              </div>
            )}
            <div className="onboarding-process-wrapper-bottom__button">
              <Button type="text" onClick={onContinueOnboarding}>
                {copies?.button}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
});
